import React, { useState, useEffect } from "react";
import { Flex, Text, Button, Dialog } from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";
import { ActiveIntegrateCard, PermissionAlertDialog } from "components";
import { useProfileContext } from "context"; // adjust path if needed
import { useToast } from "../../../context/ToastContext";
import { useUpdateComo, useGetComoConfig, useDeactivateComo } from "hooks";

export function ActiveComoIntegration() {
  const queryClient = useQueryClient();
  const { userProfile } = useProfileContext() || {};
  const { setToastMessage } = useToast();
  const [apiKey, setApiKey] = useState("");
  const [hasStoredApiKey, setHasStoredApiKey] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [isApiSaving, setIsApiSaving] = useState(false);
  const [isApiKeyValid, setIsApiKeyValid] = useState(true);
  const [apiValidationMessage, setApiValidationMessage] = useState(null);
  const [apiSaveSuccess, setApiSaveSuccess] = useState(false);

  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);

  const isOwner = (userProfile?.user_role || "") === "Owner";

  const deactivateComo = useDeactivateComo();
  const updateComo = useUpdateComo();
  const { data: comoConfig, isLoading } = useGetComoConfig(userProfile?.organisation_id);

  useEffect(() => {
    const masked = comoConfig?.data?.data?.api_key ?? "";
    setApiKey(masked);
    setHasStoredApiKey(Boolean(masked && masked.includes("*")));
    setIsApiKeyValid(true);
    setApiValidationMessage(null);
  }, [comoConfig]);

  const handleApiKeyChange = (e) => {
    const value = e?.target?.value ?? "";
    setApiKey(value);
    setIsApiKeyValid(true);
    setApiValidationMessage(null);
  };

  const handleSaveApiKey = async () => {
    if (!userProfile?.organisation_id) {
      setToastMessage("Organisation ID missing");
      setApiSaveSuccess(false);
      return;
    }

    // If the input currently contains masking chars, treat as "no change"
    if (apiKey && apiKey.includes("*")) {
      setIsApiKeyValid(false);
      setApiValidationMessage("Please enter a new API key.");
      setApiSaveSuccess(false);
      setToastMessage("No changes to API key");
      return;
    }

    if (!apiKey || apiKey.trim() === "") {
      setIsApiKeyValid(false);
      setApiValidationMessage("Please enter a valid API key.");
      setApiSaveSuccess(false);
      setToastMessage("API key cannot be empty");
      return;
    }

    setIsApiSaving(true);
    setIsApiKeyValid(true);
    setApiValidationMessage(null);

    try {
      const result = await updateComo.mutateAsync({
        api_key: apiKey.trim(),
        organisation_id: userProfile.organisation_id,
      });

      if (result?.data?.status === "success") {
        queryClient.invalidateQueries?.(["como-config"]);
        setToastMessage("API key validated and saved");
        setApiKey(""); // backend would return masked key on next fetch
        setHasStoredApiKey(true);
        setIsApiKeyValid(true);
        setApiValidationMessage(null);
        setApiSaveSuccess(true);
      } else {
        const msg = result?.data?.message || "Invalid API key";
        setIsApiKeyValid(false);
        setApiValidationMessage(msg);
        setToastMessage(msg);
        setApiSaveSuccess(false);
      }
    } catch (err) {
      let msg = "Failed to update API key";
      if (err?.response?.data?.message) {
        msg = err.response.data.message;
      }
      setIsApiKeyValid(false);
      setApiValidationMessage(msg);
      setToastMessage(msg);
      setApiSaveSuccess(false);
    } finally {
      setIsApiSaving(false);
    }
  };

  const handleDisconnectClick = () => {
    if (isOwner) setShowConfirmDialog(true);
    else setShowPermissionAlert(true);
  };

  const handleDeactivate = async () => {
    try {
      setIsDeactivating(true);
      const result = await deactivateComo.mutateAsync({
        organisation_id: userProfile?.organisation_id,
      });
      if (result?.data?.status === "success") {
        queryClient.invalidateQueries?.(["como-config"]);
        setToastMessage("Como integration disconnected");
      } else {
        throw new Error(result?.data?.message || "Failed to disconnect Como");
      }
    } catch (err) {
      setToastMessage(err?.message || "Failed to disconnect Como");
    } finally {
      setIsDeactivating(false);
      setShowConfirmDialog(false);
    }
  };

  return (
    <>
      <ActiveIntegrateCard.Root
        name="Como"
        subtitle="Connect your Como API credentials"
        image_url="como.svg"
        option={{
          text: "Disconnect",
          color: "red",
          onClick: handleDisconnectClick,
        }}
      >
        {/* API Key section */}
        <ActiveIntegrateCard.Section
          name="API key"
          subtitle="Update the Como API key used for this integration"
        >
          <Flex direction="column" gap="3" style={{ width: "100%" }}>
            <Flex direction="row" align="center" gap="2" style={{ width: "100%" }}>
              <input
                value={apiKey}
                onChange={handleApiKeyChange}
                type={showApiKey ? "text" : "password"}
                placeholder={hasStoredApiKey ? "Stored API key (hidden)" : "Enter Como API key"}
                aria-label="Como API key"
                disabled={!isOwner}
                style={{
                  flex: 1,
                  height: 36,
                  padding: "0 10px",
                  borderRadius: 6,
                  border: `1px solid ${isApiKeyValid ? "var(--gray-a7)" : "var(--red-8)"}`,
                  boxSizing: "border-box",
                  background: isOwner ? "var(--white)" : "var(--gray-3)",
                }}
              />

              <Button size="1" variant="soft" onClick={() => setShowApiKey((s) => !s)}>
                {showApiKey ? "Hide" : "Show"}
              </Button>

              <Button
                size="1"
                variant="solid"
                color="blue"
                onClick={handleSaveApiKey}
                loading={isApiSaving}
                disabled={!isOwner || isApiSaving}
              >
                Save changes
              </Button>
            </Flex>

            {!isApiKeyValid && (
              <Text size="1" color="red">
                {apiValidationMessage || "Please enter a valid API key."}
              </Text>
            )}

            {apiSaveSuccess && (
              <Text size="1" color="green">
                API key validated and saved successfully.
              </Text>
            )}

            {hasStoredApiKey && (
              <Text size="2" style={{ color: "var(--gray-a9)" }}>
                An API key is stored (hidden). Enter a new key to update it, or leave as-is.
              </Text>
            )}

            <Text size="2" style={{ color: "var(--gray-a9)" }}>
              Changing the API key will validate it immediately.
            </Text>
          </Flex>
        </ActiveIntegrateCard.Section>
      </ActiveIntegrateCard.Root>

      {/* Confirm disconnect dialog */}
      <Dialog.Root open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <Dialog.Content size="3" style={{ maxWidth: 450 }}>
          <Dialog.Title>Disconnect Como</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Customer data will stop syncing with Como.
          </Dialog.Description>
          <Flex gap="3" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">Cancel</Button>
            </Dialog.Close>
            <Button variant="solid" color="red" onClick={handleDeactivate} loading={isDeactivating} disabled={isDeactivating}>
              Disconnect
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Permission Alert */}
      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
    </>
  );
}
